import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Typography, alpha, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, LabelList } from 'recharts'
import { toast } from 'react-toastify'
import DashboardServices from 'services/DashboardServices'
import DashboardContainer from 'components/DashboardContainer'

const formatStatusLabel = (value = '') => {
    const normalized = String(value || '').replaceAll('_', ' ').trim()

    if (!normalized) {
        return 'Unknown'
    }

    return normalized
        .split(/\s+/)
        .map((part) => {
            if (!part) return part
            if (part.toLowerCase() === 'ncc') return 'NCC'
            return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
        })
        .join(' ')
}

const normalizeStatusKey = (value = '') => String(value || '').replaceAll('_', ' ').replace(/\s+/g, ' ').trim().toLowerCase()

const getDocumentStatusRows = (response = {}) => {
    return (
        response?.data?.data?.document_versions_by_status ||
        response?.data?.data?.data?.document_versions_by_status ||
        response?.data?.document_versions_by_status ||
        []
    )
}

const DocumentStatusOverview = ({ filters = {} }) => {
    const theme = useTheme()
    const isSmDown = useMediaQuery(theme.breakpoints.down('sm'))
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(false)

    const requestFilters = useMemo(() => ({
        ...filters,
       // document_status: filters.document_status || 'uploaded',
        //review_status: filters.review_status || 'pending',
    }), [filters])

    const getData = useCallback(async () => {
        setLoading(true)
        try {
            const response = await DashboardServices.getDocumentVersionsByStatus(requestFilters)

            if (response.success) {
                const rows = getDocumentStatusRows(response)
                const aggregated = rows.reduce((accumulator, item) => {
                    const key = normalizeStatusKey(item?.document_status)
                    const existing = accumulator.get(key)
                    const nextCount = Number(item?.count) || 0

                    if (existing) {
                        existing.count += nextCount
                    } else {
                        accumulator.set(key, {
                            document_status: formatStatusLabel(item?.document_status),
                            count: nextCount,
                        })
                    }

                    return accumulator
                }, new Map())

                const chartData = Array.from(aggregated.values())
                    .sort((left, right) => right.count - left.count)
                    .map((item) => ({
                        ...item,
                        countLabel: item.count.toLocaleString(),
                    }))

                setData(chartData)
            } else {
                setData([])
                toast.error(response.message || 'Failed to load document status overview.')
            }
        } finally {
            setLoading(false)
        }
    }, [requestFilters])

    useEffect(() => {
        getData()
    }, [getData])

    const totalDocuments = useMemo(
        () => data.reduce((sum, item) => sum + (Number(item.count) || 0), 0),
        [data]
    )

    const CustomTooltip = ({ active, payload }) => {
        if (!active || !payload?.length) {
            return null
        }

        const item = payload[0].payload

        return (
            <Box
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
                    borderRadius: 2,
                    px: 1.5,
                    py: 1,
                    boxShadow: theme.shadows[4],
                }}
            >
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: theme.palette.text.primary }}>
                    {item.document_status}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: theme.palette.primary.main, fontWeight: 600 }}>
                    {item.countLabel} document{item.count !== 1 ? 's' : ''}
                </Typography>
            </Box>
        )
    }

    return (
        <DashboardContainer
            title="Document Status Overview"
            action={
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: theme.palette.text.secondary }}>
                    Total {totalDocuments.toLocaleString()}
                </Typography>
            }
        >
            {loading && (
                <Box sx={{ minHeight: { xs: 280, sm: 320 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ color: theme.palette.text.secondary }}>
                        Loading document status data...
                    </Typography>
                </Box>
            )}

            {!loading && data.length === 0 && (
                <Box sx={{ minHeight: { xs: 280, sm: 320 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ color: theme.palette.text.secondary, textAlign: 'center' }}>
                        No document status data available for the selected period.
                    </Typography>
                </Box>
            )}

            {!loading && data.length > 0 && (
                <Box sx={{ width: '100%', height: { xs: 320, sm: 340, md: 360 }, display: 'flex', flexDirection: 'column' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={data}
                            margin={{ top: 20, right: 16, left: 0, bottom: isSmDown ? 48 : 28 }}
                            barSize={isSmDown ? 18 : 28}
                        >
                            <defs>
                                <linearGradient id="documentStatusPrimary" x1="0" y1="0" x2="0" y2="1">
                                    
                                    {/* <stop offset="0.2%" stopColor={theme.palette.primary.light} /> */}
                                    <stop offset="100%" stopColor={theme.palette.primary.main} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={alpha(theme.palette.text.primary, 0.08)} />
                            <XAxis
                                dataKey="document_status"
                                tick={{ fill: theme.palette.text.secondary, fontSize: isSmDown ? 10 : 12 }}
                                tickLine={false}
                                axisLine={false}
                                interval={0}
                                angle={isSmDown ? -35 : 0}
                                textAnchor={isSmDown ? 'end' : 'middle'}
                                height={isSmDown ? 64 : 42}
                            />
                            <YAxis
                                allowDecimals={false}
                                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                                tickLine={false}
                                axisLine={false}
                                width={40}
                            />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: alpha(theme.palette.primary.main, 0.08) }} />
                            <Bar dataKey="count" fill="url(#documentStatusPrimary)" radius={[4, 4, 0, 0]}>
                                <LabelList dataKey="countLabel" position="top" fill={theme.palette.text.secondary} fontSize={11} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            )}
        </DashboardContainer>
    )
}

export default DocumentStatusOverview